import { Input, Select, Tooltip } from "antd";
import EmptyState from "../EmptyState";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  InfoCircleOutlined,
  MinusOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import jMoment from "moment-jalaali";
import styles from "./styles.module.css";

jMoment.loadPersian?.({ dialect: "persian-modern" });

const HEADER_H = 56;
const ROW_H = 46;
const MAX_BODY_H = 540;
const NAME_W = 190;
const DATE_W = 78;
const TABLE_W = NAME_W + DATE_W * 2;
const DEFAULT_COLOR = "#6366f1";

const BASE_DAY_W = { day: 42, week: 16, month: 5 };
const ZOOM_STEPS = [0.5, 0.65, 0.8, 1, 1.25, 1.5, 2];

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const dayDiff = (a, b) =>
  Math.round((startOfDay(a).getTime() - startOfDay(b).getTime()) / 86400000);
const addDays = (d, n) => {
  const x = startOfDay(d);
  x.setDate(x.getDate() + n);
  return x;
};
const jFmt = (d, fmt) => jMoment(d).locale("fa").format(fmt);

const contrastText = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!m) return "#ffffff";
  const rgb = parseInt(m[1], 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 150 ? "#0f172a" : "#ffffff";
};

const buildPeriods = (timelineStart, totalDays, unit, labelFmt) => {
  const out = [];
  const tEnd = addDays(timelineStart, totalDays - 1);
  let cur = jMoment(timelineStart).startOf(unit);
  const guard = jMoment(tEnd).endOf(unit);
  let safety = 0;
  while (cur.isSameOrBefore(guard, "day") && safety++ < 5000) {
    const next = cur.clone().add(1, unit);
    const segStartDate =
      cur.toDate() < timelineStart ? timelineStart : cur.toDate();
    const rawEnd = addDays(next.toDate(), -1);
    const segEndDate = rawEnd > tEnd ? tEnd : rawEnd;
    const startOffset = dayDiff(segStartDate, timelineStart);
    const span = dayDiff(segEndDate, segStartDate) + 1;
    if (span > 0)
      out.push({
        key: cur.format(labelFmt) + "-" + startOffset,
        startOffset,
        span,
        label: cur.locale("fa").format(labelFmt),
      });
    cur = next;
  }
  return out;
};

const GanttChart = ({
  rows: rawRows = [],
  title = "نمودار گانت",
  editable = false,
  onRowChange,
  onRowClick,
  nameHeader = "نام وظیفه",

  titleHint,
  /* When set, the «i» beside the title becomes a button that calls this instead
   * of a tooltip. A hint you have to hover to read and a walkthrough you have to
   * find elsewhere are two half-answers; one icon, one click. */
  onHelp,
  enableSearch = true,
  searchPlaceholder = "جستجو",
  initialScale = "week",
  toolbarExtra,
  emptyText = "موردی برای نمایش یافت نشد",
  /* [{ from, to }] in row ids. Drawn as arrows from the end of `from` to the
   * start of `to` — what a Gantt exists for, and what this chart did not have:
   * without links it is a bar chart of dates, which the calendar already is. */
  dependencies = [],
  /* Row ids on the longest dependent chain. Highlighted rather than recoloured,
   * so the step colours still mean what they mean everywhere else. */
  criticalIds,
  /* Called when something is dropped onto the timeline, with the day it landed
   * on. Used by the unscheduled tray to turn a drop into a date. */
  onTimelineDrop,
}) => {
  const [scale, setScale] = useState(initialScale);
  const [zoomIdx, setZoomIdx] = useState(3);
  const [search, setSearch] = useState("");
  const [drag, setDrag] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const headViewportRef = useRef(null);
  const timelineBodyRef = useRef(null);
  const tableBodyRef = useRef(null);
  const dragRef = useRef(null);

  /* Row order, decided once per set of rows rather than on every date change.
   *
   * Sorting by start date on every render meant dragging a bar reshuffled the
   * chart under the cursor: the row you were holding jumped to a different
   * line the moment its dates crossed a neighbour's. The order is still
   * chronological when the rows arrive — it just stops moving while you edit
   * them, and is recomputed when a task is added, removed or filtered out. */
  const orderRef = useRef({ key: "", order: new Map() });

  const rows = useMemo(() => {
    const mapped = (rawRows || [])
      .filter((r) => r?.start && r?.end)
      .map((r) => {
        const start = startOfDay(r.start);
        let end = startOfDay(r.end);
        if (end < start) end = start;
        return {
          ...r,
          title: r?.title || "بدون عنوان",
          color: r?.color || DEFAULT_COLOR,
          progress: Math.max(0, Math.min(100, Number(r?.progress) || 0)),
          start,
          end,
        };
      });

    /* Identity of the set, not of its contents: dates are exactly what must not
     * invalidate the order. */
    const key = mapped
      .map((r) => r.id)
      .sort()
      .join(",");

    if (orderRef.current.key !== key) {
      const chronological = [...mapped].sort((a, b) => a.start - b.start);
      orderRef.current = {
        key,
        order: new Map(chronological.map((r, i) => [r.id, i])),
      };
    }

    const { order } = orderRef.current;
    const term = search.trim().toLowerCase();

    return mapped
      .filter((r) => !term || (r?.title || "").toLowerCase().includes(term))
      .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
  }, [rawRows, search]);

  const { timelineStart, totalDays, dayWidth, totalWidth } = useMemo(() => {
    const dw = BASE_DAY_W[scale] * ZOOM_STEPS[zoomIdx];
    if (rows.length === 0) {
      const s = startOfDay(new Date());
      return {
        timelineStart: addDays(s, -7),
        totalDays: 30,
        dayWidth: dw,
        totalWidth: 30 * dw,
      };
    }
    let min = rows[0].start;
    let max = rows[0].end;
    rows.forEach((r) => {
      if (r.start < min) min = r.start;
      if (r.end > max) max = r.end;
    });
    const pad = { day: 3, week: 7, month: 20 }[scale];
    const start = addDays(min, -pad);
    const end = addDays(max, pad);
    const days = Math.max(1, dayDiff(end, start) + 1);
    return {
      timelineStart: start,
      totalDays: days,
      dayWidth: dw,
      totalWidth: days * dw,
    };
  }, [rows, scale, zoomIdx]);

  const { majorCells, minorCells } = useMemo(() => {
    let major, minor;
    if (scale === "day") {
      major = buildPeriods(timelineStart, totalDays, "jMonth", "jMMMM jYYYY");
      minor = [];
      for (let i = 0; i < totalDays; i++) {
        const d = addDays(timelineStart, i);
        minor.push({
          key: "d" + i,
          startOffset: i,
          span: 1,
          label: jFmt(d, "jD"),
          weekend: jMoment(d).day() === 5,
          today: dayDiff(d, new Date()) === 0,
        });
      }
    } else if (scale === "week") {
      major = buildPeriods(timelineStart, totalDays, "jMonth", "jMMMM jYYYY");
      minor = [];
      for (let off = 0; off < totalDays; off += 7) {
        const d = addDays(timelineStart, off);
        minor.push({
          key: "w" + off,
          startOffset: off,
          span: Math.min(7, totalDays - off),
          label: jFmt(d, "jD jMMMM"),
        });
      }
    } else {
      major = buildPeriods(timelineStart, totalDays, "jYear", "jYYYY");
      minor = buildPeriods(timelineStart, totalDays, "jMonth", "jMMMM");
    }
    return { majorCells: major, minorCells: minor };
  }, [scale, timelineStart, totalDays]);

  const todayOffset = dayDiff(new Date(), timelineStart);
  const showToday = todayOffset >= 0 && todayOffset < totalDays;
  const bodyH = Math.min(rows.length * ROW_H, MAX_BODY_H);

  const criticalSet = useMemo(
    () => new Set(criticalIds || []),
    [criticalIds],
  );

  /* Arrow geometry, in the same right-anchored pixel space the bars use — the
   * chart is RTL, so "right" is the start of the timeline and x grows leftwards
   * as time moves forward. */
  const links = useMemo(() => {
    if (!dependencies?.length || !rows.length) return [];

    const index = new Map(rows.map((row, i) => [row.id, { row, i }]));

    return dependencies
      .map(({ from, to }) => {
        const a = index.get(from);
        const b = index.get(to);
        if (!a || !b) return null;

        /* From the end of the blocker to the start of the blocked task. */
        const fromX = (dayDiff(a.row.end, timelineStart) + 1) * dayWidth;
        const toX = dayDiff(b.row.start, timelineStart) * dayWidth;
        const fromY = a.i * ROW_H + ROW_H / 2;
        const toY = b.i * ROW_H + ROW_H / 2;

        return {
          key: `${from}-${to}`,
          fromX,
          fromY,
          toX,
          toY,
          critical: criticalSet.has(from) && criticalSet.has(to),
          /* The blocked task starting before its blocker ends is the thing a
           * Gantt is supposed to make impossible to miss. */
          violated: b.row.start < a.row.end,
        };
      })
      .filter(Boolean);
  }, [dependencies, rows, timelineStart, dayWidth, criticalSet]);

  const syncFromTimeline = useCallback(() => {
    const tl = timelineBodyRef.current;
    if (!tl) return;
    if (headViewportRef.current)
      headViewportRef.current.scrollLeft = tl.scrollLeft;
    if (tableBodyRef.current) tableBodyRef.current.scrollTop = tl.scrollTop;
  }, []);
  const syncFromTable = useCallback(() => {
    if (tableBodyRef.current && timelineBodyRef.current)
      timelineBodyRef.current.scrollTop = tableBodyRef.current.scrollTop;
  }, []);

  const beginDrag = (e, row, mode) => {
    if (!editable) return;
    e.preventDefault();
    e.stopPropagation();
    const offset = dayDiff(row.start, timelineStart);
    const duration = dayDiff(row.end, row.start) + 1;
    const state = {
      id: row.id,
      mode,
      startX: e.clientX,
      origOffset: offset,
      origDuration: duration,
      offset,
      duration,
      moved: false,
      row,
    };
    dragRef.current = state;
    setActiveId(row.id);

    const onMove = (ev) => {
      const st = dragRef.current;
      if (!st) return;

      const deltaRight = st.startX - ev.clientX;
      const deltaDays = Math.round(deltaRight / dayWidth);
      if (Math.abs(ev.clientX - st.startX) > 4) st.moved = true;
      let offset = st.origOffset;
      let duration = st.origDuration;
      if (st.mode === "move") {
        offset = st.origOffset + deltaDays;
      } else if (st.mode === "resize-start") {
        const dd = Math.min(deltaDays, st.origDuration - 1);
        offset = st.origOffset + dd;
        duration = st.origDuration - dd;
      } else if (st.mode === "resize-end") {
        duration = Math.max(1, st.origDuration + deltaDays);
      }
      st.offset = offset;
      st.duration = duration;
      setDrag({ id: st.id, mode: st.mode, offset, duration });
    };

    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      const st = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (!st) return;
      if (!st.moved) {
        onRowClick?.(st.row);
        return;
      }
      const newStart = addDays(timelineStart, st.offset);
      const newEnd = addDays(newStart, st.duration - 1);
      onRowChange?.(st.row, { start: newStart, end: newEnd });
    };

    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };

  const Toolbar = (
    <div className={styles.toolbar}>
      {/* Falsy title means the caller already provides a header (e.g. ChartCard). */}
      {title && (
        <h5 className={styles.toolbarTitle}>
          <span className={styles.dot} />
          {title}
          {onHelp ? (
            <button
              type="button"
              className={styles.titleHelp}
              onClick={onHelp}
              aria-label="راهنما"
            >
              <InfoCircleOutlined />
            </button>
          ) : (
            titleHint && (
              <Tooltip title={titleHint}>
                <InfoCircleOutlined className={styles.titleHint} />
              </Tooltip>
            )
          )}
        </h5>
      )}
      <div className={styles.toolbarActions}>
        {enableSearch && (
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: "var(--ds-placeholder)" }} />}
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 180 }}
          />
        )}
        <div className={styles.zoomGroup}>
          <button
            type="button"
            className={styles.zoomBtn}
            disabled={zoomIdx <= 0}
            onClick={() => setZoomIdx((i) => Math.max(0, i - 1))}
            title="کوچک‌نمایی"
          >
            <MinusOutlined />
          </button>
          <span className={styles.zoomLabel}>
            {Math.round(ZOOM_STEPS[zoomIdx] * 100)}%
          </span>
          <button
            type="button"
            className={styles.zoomBtn}
            disabled={zoomIdx >= ZOOM_STEPS.length - 1}
            onClick={() =>
              setZoomIdx((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))
            }
            title="بزرگ‌نمایی"
          >
            <PlusOutlined />
          </button>
        </div>
        <Select
          value={scale}
          onChange={setScale}
          style={{ width: 110 }}
          options={[
            { value: "day", label: "روزانه" },
            { value: "week", label: "هفتگی" },
            { value: "month", label: "ماهانه" },
          ]}
        />
        {toolbarExtra}
      </div>
    </div>
  );

  if (rows.length === 0) {
    /* An empty chart still has to accept a drop, or the tray is unusable on
     * exactly the project that needs it most: one where nothing is scheduled
     * yet. There is no timeline to read a date off, so the drop lands on today
     * and the user drags the bar from there. */
    return (
      <div className={styles.wrapper}>
        {Toolbar}
        <div
          className={`${styles.empty} ${onTimelineDrop ? styles.emptyDroppable : ""}`}
          onDragOver={onTimelineDrop ? (e) => e.preventDefault() : undefined}
          onDrop={
            onTimelineDrop
              ? (event) => {
                  event.preventDefault();
                  const payload = event.dataTransfer.getData("text/plain");
                  if (payload) onTimelineDrop(payload, startOfDay(new Date()));
                }
              : undefined
          }
        >
          <EmptyState
            compact
            filtered={Boolean(search.trim())}
            title="تسکی برای نمایش نیست"
            description={
              onTimelineDrop
                ? `${emptyText} — یکی از تسک‌های «بدون زمان‌بندی» رو بکش این‌جا تا از امروز شروع بشه.`
                : emptyText
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {Toolbar}

      <div className={styles.board}>
        {}
        <div className={styles.headerRow}>
          <div
            className={styles.tableHead}
            style={{ width: TABLE_W, height: HEADER_H }}
          >
            <div className={styles.thCell} style={{ width: NAME_W }}>
              {nameHeader}
            </div>
            <div
              className={`${styles.thCell} ${styles.dateCell}`}
              style={{ width: DATE_W }}
            >
              شروع
            </div>
            <div
              className={`${styles.thCell} ${styles.dateCell}`}
              style={{ width: DATE_W }}
            >
              پایان
            </div>
          </div>
          <div className={styles.timelineHeadViewport} ref={headViewportRef}>
            <div
              className={styles.headInner}
              style={{ width: totalWidth, height: HEADER_H }}
            >
              <div className={styles.majorRow}>
                {majorCells.map((c) => (
                  <div
                    key={c.key}
                    className={styles.majorCell}
                    style={{
                      right: c.startOffset * dayWidth,
                      width: c.span * dayWidth,
                    }}
                  >
                    {c.label}
                  </div>
                ))}
              </div>
              <div className={styles.minorRow}>
                {minorCells.map((c) => (
                  <div
                    key={c.key}
                    className={`${styles.minorCell} ${
                      c.weekend ? styles.weekend : ""
                    } ${c.today ? styles.today : ""}`}
                    style={{
                      right: c.startOffset * dayWidth,
                      width: c.span * dayWidth,
                    }}
                  >
                    {c.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {}
        <div className={styles.bodyRow}>
          <div
            className={styles.tableBody}
            style={{ width: TABLE_W, height: bodyH }}
            ref={tableBodyRef}
            onScroll={syncFromTable}
          >
            {rows.map((r) => (
              <div
                key={r.id}
                className={`${styles.tRow} ${
                  activeId === r.id ? styles.active : ""
                }`}
                style={{ height: ROW_H }}
                onMouseEnter={() => setActiveId(r.id)}
                onMouseLeave={() =>
                  setActiveId((cur) => (cur === r.id ? null : cur))
                }
              >
                <div className={styles.tdCell} style={{ width: NAME_W }}>
                  <span className={styles.taskName}>
                    <span
                      className={styles.swatch}
                      style={{ background: r.color }}
                    />
                    <span
                      className={styles.nameText}
                      onClick={() => onRowClick?.(r)}
                      title={r.title}
                    >
                      {r.title}
                    </span>
                  </span>
                </div>
                <div
                  className={`${styles.tdCell} ${styles.dateCell}`}
                  style={{ width: DATE_W }}
                >
                  {jFmt(r.start, "jYYYY/jMM/jDD")}
                </div>
                <div
                  className={`${styles.tdCell} ${styles.dateCell}`}
                  style={{ width: DATE_W }}
                >
                  {jFmt(r.end, "jYYYY/jMM/jDD")}
                </div>
              </div>
            ))}
          </div>

          <div
            className={`${styles.timelineBody} ds-scroll-compact`}
            style={{ height: bodyH }}
            ref={timelineBodyRef}
            onScroll={syncFromTimeline}
            onDragOver={onTimelineDrop ? (e) => e.preventDefault() : undefined}
            onDrop={
              onTimelineDrop
                ? (event) => {
                    event.preventDefault();
                    const payload = event.dataTransfer.getData("text/plain");
                    if (!payload) return;

                    /* The chart is RTL: the distance from the right edge of the
                     * scrolled content is what maps to elapsed days. */
                    const box = event.currentTarget.getBoundingClientRect();
                    const fromRight =
                      box.right - event.clientX + event.currentTarget.scrollLeft;
                    const day = Math.max(0, Math.floor(fromRight / dayWidth));

                    onTimelineDrop(payload, addDays(timelineStart, day));
                  }
                : undefined
            }
          >
            <div
              className={styles.bodyInner}
              style={{ width: totalWidth, height: rows.length * ROW_H }}
            >
              {}
              {minorCells.map((c) => (
                <div
                  key={"g" + c.key}
                  className={`${styles.gridLine} ${
                    scale !== "day" ? styles.major : ""
                  }`}
                  style={{ right: c.startOffset * dayWidth }}
                />
              ))}
              {}
              {scale === "day" &&
                minorCells
                  .filter((c) => c.weekend)
                  .map((c) => (
                    <div
                      key={"wb" + c.key}
                      className={styles.weekendBand}
                      style={{
                        right: c.startOffset * dayWidth,
                        width: dayWidth,
                      }}
                    />
                  ))}
              {}
              {rows.map((r, i) => (
                <div key={"rl" + r.id}>
                  {i % 2 === 1 && (
                    <div
                      className={styles.rowStripe}
                      style={{ top: i * ROW_H, height: ROW_H }}
                    />
                  )}
                  <div
                    className={styles.rowLine}
                    style={{ top: (i + 1) * ROW_H - 1 }}
                  />
                </div>
              ))}
              {}
              {showToday && (
                <div
                  className={styles.todayLine}
                  style={{ right: todayOffset * dayWidth }}
                />
              )}
              {}
              {links.length > 0 && (
                <svg
                  className={styles.linkLayer}
                  width={totalWidth}
                  height={rows.length * ROW_H}
                >
                  <defs>
                    <marker
                      id="gantt-arrow"
                      viewBox="0 0 8 8"
                      refX="7"
                      refY="4"
                      markerWidth="7"
                      markerHeight="7"
                      orient="auto-start-reverse"
                    >
                      <path d="M0,0 L8,4 L0,8 z" fill="context-stroke" />
                    </marker>
                  </defs>

                  {links.map((link) => {
                    /* Right-anchored coordinates, mirrored into the svg's own
                     * left-to-right space. */
                    const x1 = totalWidth - link.fromX;
                    const x2 = totalWidth - link.toX;
                    /* An elbow rather than a straight line: two tasks on
                       neighbouring rows would otherwise draw a line through the
                       bar between them. */
                    const mid = x1 + Math.max(12, (x2 - x1) / 2);

                    return (
                      <polyline
                        key={link.key}
                        className={`${styles.link} ${
                          link.violated ? styles.linkViolated : ""
                        } ${link.critical ? styles.linkCritical : ""}`}
                        points={`${x1},${link.fromY} ${mid},${link.fromY} ${mid},${link.toY} ${x2},${link.toY}`}
                        markerEnd="url(#gantt-arrow)"
                      />
                    );
                  })}
                </svg>
              )}
              {}
              {rows.map((r, i) => {
                const isDragging = drag?.id === r.id;
                const offset = isDragging
                  ? drag.offset
                  : dayDiff(r.start, timelineStart);
                const duration = isDragging
                  ? drag.duration
                  : dayDiff(r.end, r.start) + 1;
                const right = offset * dayWidth;
                const width = Math.max(dayWidth, duration * dayWidth);
                const textColor = contrastText(r.color);
                const labelFits = width > 60;
                const s = addDays(timelineStart, offset);
                const e = addDays(s, duration - 1);
                return (
                  <Tooltip
                    key={"bar" + r.id}
                    title={`${r.title} • ${jFmt(s, "jD jMMMM")} تا ${jFmt(
                      e,
                      "jD jMMMM",
                    )} • ${r.progress}%`}
                    mouseEnterDelay={0.35}
                  >
                    <div
                      className={`${styles.bar} ${
                        isDragging ? styles.dragging : ""
                      } ${!editable ? styles.readonly : ""} ${
                        criticalSet.has(r.id) ? styles.barCritical : ""
                      }`}
                      style={{
                        right,
                        width,
                        top: i * ROW_H + (ROW_H - 28) / 2,
                        height: 28,
                        background: r.color,
                        color: textColor,
                      }}
                      onPointerDown={(ev) =>
                        editable ? beginDrag(ev, r, "move") : undefined
                      }
                      onClick={() => !editable && onRowClick?.(r)}
                    >
                      <div
                        className={styles.barProgress}
                        style={{ width: `${r.progress}%` }}
                      />
                      {labelFits ? (
                        <span className={styles.barLabel}>
                          {r.title}
                          {r.progress > 0 && (
                            <span className={styles.barPct}>{r.progress}%</span>
                          )}
                        </span>
                      ) : (
                        <span
                          className={styles.barLabelOutside}
                          style={{ right: right + width + 8 }}
                        >
                          {r.title}
                        </span>
                      )}
                      {editable && (
                        <>
                          <div
                            className={`${styles.handle} ${styles.handleStart}`}
                            onPointerDown={(ev) =>
                              beginDrag(ev, r, "resize-start")
                            }
                          />
                          <div
                            className={`${styles.handle} ${styles.handleEnd}`}
                            onPointerDown={(ev) => beginDrag(ev, r, "resize-end")}
                          />
                        </>
                      )}
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GanttChart;
